import { usePersistedStorage } from '@/composables/UsePersistedStorage'
import { useTokenStore } from '@/store/tokens'
import { useEndUserProfileStore } from '@/store/user-profile'
import Sws from '@serato/sws-sdk'
import { SwsConfiguration, Identity } from '@serato/sws-sdk'

type GetSwsSdk = () => Sws

class StorageKeys {
  private appId: string
  constructor (appId: string) {
    this.appId = appId
  }
  get accessToken (): string {
    return 'auth.accessToken.' + this.appId
  }
  get refreshToken (): string {
    return 'auth.refreshToken.' + this.appId
  }
  get state (): string {
    return 'auth.state.' + this.appId
  }
  get codeVerifier (): string {
    return 'auth.codeVerifier.' + this.appId
  }
  get redirectUrl (): string {
    return 'auth.redirectUrl.' + this.appId
  }
}

const swsConfig: SwsConfiguration = {
  appId: process.env.VUE_APP_SWS_APP_ID ?? '',
  serviceUri: {
    id: process.env.VUE_APP_SWS_SERVICE_URI_ID,
    license: process.env.VUE_APP_SWS_SERVICE_URI_LICENSE,
    ecom: process.env.VUE_APP_SWS_SERVICE_URI_ECOM,
    profile: process.env.VUE_APP_SWS_SERVICE_URI_PROFILE,
    da: process.env.VUE_APP_SWS_SERVICE_URI_DA,
    notifications: process.env.VUE_APP_SWS_SERVICE_URI_NOTIFICATIONS,
    rewards: process.env.VUE_APP_SWS_SERVICE_URI_REWARDS,
    aiproxy: process.env.VUE_APP_SWS_SERVICE_URI_AI_PROXY,
    cloudlib: process.env.VUE_APP_SWS_SERVICE_URI_CLOUDLIB
  },
  timeout: 30000
}

const { persistedStorage } = usePersistedStorage()
const storageKeys = new StorageKeys(swsConfig.appId)

export const logout = () => {
  // Deleting refresh token and access token in local storage
  const appId = swsConfig.appId
  const serviceUriId = swsConfig.serviceUri?.id
  
  if (appId && serviceUriId) {
    persistedStorage.removeItem(storageKeys.refreshToken)
    
    const url = serviceUriId + '/en/logout' + '?app_id=' + appId + '&?continue='
    window.location.href = url
  }
}

const getSwsSdk: GetSwsSdk = (): Sws => {

  const endUserProfileStore = useEndUserProfileStore()
  const sws = new Sws(swsConfig)

  const searchParams = new URLSearchParams(window.location.search)

  // Has the user been redirected back to app after authorising?
  if (searchParams.has('state') && searchParams.has('code')) {
    const authFlowComplete = () => {
      // Push state to the original URL (ie. without the `state` and `code` URL parameters)
      window.history.pushState({ title: document.title }, '', persistedStorage.getItem(storageKeys.redirectUrl))
      // Invalidate transient items in storage
      persistedStorage.removeItem(storageKeys.state)
      persistedStorage.removeItem(storageKeys.codeVerifier)
      persistedStorage.removeItem(storageKeys.redirectUrl)
    }
    if (searchParams.get('state') === persistedStorage.getItem(storageKeys.state)) {
      // Exchange `code`, `redirectUrl` and `codeVerifier` for tokens
      sws.id.tokenExchange(
        searchParams.get('code') ?? '',
        persistedStorage.getItem(storageKeys.redirectUrl) ?? '',
        persistedStorage.getItem(storageKeys.codeVerifier) ?? ''
      ).then((userLogin: Identity.UserLogin) => {
        // Write refresh token to storage
        persistedStorage.setItem(storageKeys.refreshToken, userLogin.tokens.refresh.token)

        authFlowComplete()

        // Finish by configuring the Sws client instance
        configureSwsClient(sws)
        endUserProfileStore.fetchProfile(sws)
      }).catch((err) => {
        authFlowComplete()
        // TODO
        console.log(['tokenExchange error', err])
      })
    } else {
      // TODO
      console.log('`state` does not match')
    }
  } else if (persistedStorage.getItem(storageKeys.refreshToken) === null) {
    // No refresh token. So need to authorise the app.
    appAuthStart(sws)
  } else {
    configureSwsClient(sws)
    endUserProfileStore.fetchProfile(sws)
  }

  return sws
}

export { Sws, getSwsSdk }

function configureSwsClient (
  sws: Sws
): void {
  // Set a invalidRefreshTokenHandler callback
  sws.setInvalidRefreshTokenHandler(() => {
    appAuthStart(sws)
  })
  const tokenStore = useTokenStore()
  // Set a passwordReEntryRequiredHandler callback
  sws.setPasswordReEntryRequiredHandler(() => {

    const refreshToken = tokenStore.getRefreshToken()
    let rftid = ''
    if (refreshToken) {
      rftid = refreshToken.uuid
    }
    appAuthStart(sws, rftid)
  })

  // Add a function handler for when a new access token is received
  sws.accessTokenUpdatedHandler = (
    accessToken: string,
    _,
    refreshToken: string
  ) => {
    persistedStorage.setItem(storageKeys.refreshToken, refreshToken)
    tokenStore.setToken({ accessTokenRaw: accessToken, refreshTokenRaw: refreshToken })
  }

  sws.refreshToken = persistedStorage.getItem(storageKeys.refreshToken) ?? ''
  sws.accessToken = 'NO-ACCESS-TOKEN'

  tokenStore.setToken({ accessTokenRaw: sws.accessToken, refreshTokenRaw: sws.refreshToken })
}

/**
 * Redirects the browser to the SSO service
 */
async function appAuthStart (
  sws: Sws,
  refreshTokenId?: string
) {
  const redirectUrl = window.location.href
  const authorizationRequest = await sws.createAuthorizationRequest(redirectUrl, refreshTokenId)

  // Write items to storage
  persistedStorage.setItem(storageKeys.state, authorizationRequest.state)
  persistedStorage.setItem(storageKeys.codeVerifier, authorizationRequest.codeVerifier)
  persistedStorage.setItem(storageKeys.redirectUrl, redirectUrl)

  window.location.href = authorizationRequest.url
}